import React from 'react'
import PropTypes from 'prop-types'
import I18n from 'translations/i18n'
import classnames from 'classnames'
import { TextInput, Toggle } from '@jisr-hr/design-system'
import TimeTable from './TimeTable'
import { ReactComponent as CloseICon } from './assets/Close.svg'

import Styles from './TimePicker.module.css'

import { pad, timeToNumbers, timeGraper, isValidTime } from './functions'

const initTime = {
  ampm: 'am',
  minutes: 0,
  hour: null,
}

const TimePicker = ({
  label,
  onChange,
  onBlur,
  value: propValue,
  timeOnly,
  maxTime,
  minTime,
  nextDay,
  withResetTime,
  isClearable,
  fit,
  hideIcon,
  errorMessage,
  style,
  noLimit,
  ...rest
}) => {
  const [value, setValue] = React.useState('')
  const [time, setTime] = React.useState(initTime)
  const [openDropdown, setOpenDropdown] = React.useState()
  const [typing, setTyping] = React.useState()

  const clearable = value && isClearable
  const max = timeToNumbers(maxTime, noLimit) || null
  const min = timeToNumbers(minTime, noLimit) || null

  // input field reference
  const inputRef = React.useRef(null)

  const validate = (timeText) => {
    const inputValue = timeToNumbers(timeText, noLimit)

    const isMoreThanMax =
      max?.hour24 < inputValue?.hour24 ||
      (max?.hour24 === inputValue?.hour24 && inputValue?.minutes > max?.minutes)

    const isLessThanMin =
      min?.hour24 > inputValue?.hour24 ||
      (min?.hour24 === inputValue?.hour24 && inputValue?.minutes < min?.minutes)

    if (isMoreThanMax) {
      setTime(timeToNumbers(maxTime, noLimit))
      return maxTime
    }

    if (isLessThanMin) {
      setTime(timeToNumbers(minTime, noLimit))
      return minTime
    }

    return timeText
  }

  const timeToText = () => {
    const { ampm, hour, minutes } = time
    let val = `${pad(hour, noLimit)}:${pad(minutes)} ${
      !timeOnly && ampm ? ampm?.toUpperCase() : ''
    }`
    if (timeOnly) val = val.replace(/\s+/g, '')

    return val
  }

  // set input value
  const handleValue = () => {
    if (typing) return false
    const val = timeToText()

    const validatedValue = validate(val)

    setValue(validatedValue)
    onChange(validatedValue)

    return true
  }

  // handle on time selecting
  const handleChangeTime = (key, val) => {
    setTime({
      ...time,
      [key]: val,
    })

    inputRef.current.focus()
  }

  const handleResetTime = () => {
    setTime({
      ...initTime,
      hour24: 0,
      hour: 0,
    })
  }

  const handleClearValue = (e) => {
    e.stopPropagation()
    setValue('')
    onChange(null)
    setTime(initTime)
  }

  // show/hide the dropdown
  const dropdownToggle = () => {
    // close any dropdown opened
    // document.querySelector('body').click();

    // open dropdown
    setOpenDropdown(true)

    // close dropdown listner
    const listner = () => {
      setOpenDropdown(false)
      document.removeEventListener('click', listner)
    }

    // // close dropdown when click outside
    setTimeout(() => {
      document.addEventListener('click', listner)
    }, 100)
  }

  const setValueToTime = () => {
    if (propValue === value) return false
    if (!isValidTime(propValue, noLimit)) {
      setTime(initTime)
      setValue('')
      return false
    }

    const val = propValue.split(':')
    if (!val.length) return false

    const timeValue = timeToNumbers(propValue, noLimit)

    return setTime(timeValue)
  }

  const handleOnChange = (e) => {
    const inputValue = e.target.value

    setValue(inputValue)
    setTyping(true)
    const t = timeGraper(inputValue, timeOnly, timeOnly, withResetTime, noLimit)

    const tempTime = timeToText()

    onChange(t || tempTime || '00:00 AM')

    if (t) {
      setTime(timeToNumbers(t, noLimit))
    }

    setOpenDropdown(true)
  }

  const handleOnBlur = () => {
    setTyping(false)
    const t = timeGraper(value, timeOnly, timeOnly, withResetTime, noLimit)
    if (t) {
      setValue(t)
      onBlur(t)
    }
  }

  // set value once time selected
  React.useEffect(() => {
    if (time.hour || time.hour === 0) handleValue()
  }, [time, typing])

  React.useEffect(() => {
    setValueToTime()
  }, [propValue])

  return (
    <div
      className={classnames(
        Styles.timePicker,
        !clearable && Styles.clockIcon,
        timeOnly && Styles.timeOnly,
        (fit || timeOnly) && Styles.fit,
        hideIcon && Styles.hideIcon,
      )}
      style={style}
    >
      {nextDay && (
        <span className={classnames(Styles.nextday, !label && Styles.nextDayWithOutLabel)}>
          {nextDay}
        </span>
      )}

      <TextInput
        {...rest}
        label={label}
        ref={inputRef}
        value={value
          ?.toString()
          .toUpperCase()
          .replace('AM', I18n.t('am'))
          .replace('PM', I18n.t('pm'))}
        onClick={dropdownToggle}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        endIcon={
          clearable && (
            <CloseICon
              className="pointer"
              onClick={handleClearValue}
            />
          )
        }
      />

      {openDropdown && !timeOnly && (
        <div
          className={Styles.dropdown}
          onClick={(e) => e.stopPropagation()}
        >
          <Toggle
            list={[
              { label: I18n.t('am'), value: 'am' },
              { label: I18n.t('pm'), value: 'pm' },
            ]}
            selected={time.ampm}
            handleChange={(t) => handleChangeTime('ampm', t.value)}
          />

          <TimeTable
            time={time}
            withResetTime={withResetTime || timeOnly}
            handleResetTime={handleResetTime}
            onChange={handleChangeTime}
            pad={pad}
            maxTime={max}
            minTime={min}
            is24Hour={timeOnly}
            noLimit={noLimit}
          />
        </div>
      )}
    </div>
  )
}

TimePicker.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.string,
  withResetTime: PropTypes.bool,
  noLimit: PropTypes.bool,
  timeOnly: PropTypes.bool,
  maxTime: PropTypes.string,
  minTime: PropTypes.string,
  nextDay: PropTypes.string,
  isClearable: PropTypes.bool,
  fit: PropTypes.bool,
  hideIcon: PropTypes.bool,
  errorMessage: PropTypes.string,
  style: PropTypes.shape(),
}

TimePicker.defaultProps = {
  onChange: () => {},
  onBlur: () => {},
}

export default TimePicker
